import {
  Button,
  Spinner,
  Textarea,
  TextareaOnChangeData,
  makeStyles,
  shorthands,
  tokens
} from "@fluentui/react-components";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { MessageInfo } from "../../MessageContext";
import { sendRequest } from "../../apis/OffersApi";
import { PageLayout } from "../../components";

const useStyles = makeStyles({
  body: {
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
    maxWidth: "720px",
    ...shorthands.padding(0, "1em")
  },
  header: {
    textAlign: "center"
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "right",
    ...shorthands.margin("1em", 0)
  },
  description: {
    ...shorthands.margin(0, 0, "1em")
  },
  cancel: {
    marginRight: "2em",
    color: "#4F52B2"
  }
});

export const LicenseRequestPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [message, setMessage] = useState<MessageInfo>(null);
  const [input, setInput] = useState("");
  const [validated, setValidated] = useState(true);
  const [newOfferId] = useState(uuidv4());
  const [isLoading, setLoading] = useState<boolean>(false);
  
  const onChange = (_: any, data: TextareaOnChangeData) => {
    setInput(data.value);
    setValidated(true);
  };

  const onCancelClick = () => navigate("/tab");

  const onSubmitClick = () => {
    if (input.trim().length === 0) {
      setValidated(false);
      setMessage({
        intent: "error",
        title: "Validation error",
        description: "Fill out the form"
      });
      return;
    } 

    setLoading(true);
    sendRequest(newOfferId, input)
      .then(() => {
        setLoading(false);
        queryClient.setQueryData('notificationMessage', {
          intent: "success",
          description: "Your request has been submitted for review"
        });
        navigate("/tab");
      })
      .catch((e: Error) => {
        setLoading(false);
        setMessage({
          intent: "error",
          title: "Error",
          description: e.message
        });
      });
  };

  const onDismiss = () => setMessage(null);

  const styles = useStyles();

  return (
    <PageLayout message={message} onDismiss={onDismiss}>
      <div className={styles.body}>
        <h3 className={styles.header}>License Assistant</h3>
        <p className={styles.description}>Please describe in simple terms any issues you have encountered related to software licensing, or specify the product/feature you require. Our engineers will determine the most appropriate solution and will request approval from your company’s administrators. We will keep you updated on the decision reached.</p>
        <Textarea
          style={validated ? {} : {borderColor: "red"}}
          appearance="outline"
          placeholder="Enter your request here, for example:
I'm having trouble organizing and analyzing large data sets. Can you recommend any solutions or tools to simplify this process?"
          resize="vertical"
          onChange={onChange}
          rows={8}
        />
        <div className={styles.buttons}>
          <Button className={styles.cancel} onClick={onCancelClick} size="large">
            Cancel
          </Button>
          <Button
            disabled={isLoading}
            icon={isLoading ? <Spinner size="tiny" /> : null}
            onClick={onSubmitClick}
            size="large"
            appearance="primary">
              Submit
          </Button>
        </div>
      </div>
    </PageLayout>
  );
}
